
// Cache converted images to avoid repeated network work
import axios from "axios";
import API_BASE_URL from "./apiConfig";

const imageConversionCache = new Map();

const gets3ImageBase64 = async (proxyUrl) => {
  const response = await axios.get(
    `${API_BASE_URL}/pets/gets3Image${proxyUrl}`
  );
  return response;
};

/**
 * Convert remote image URL (e.g., S3) to a PNG data URL via canvas.
 * This normalizes formats (webp/jpg/png) and avoids cross-origin canvas taint by
 * rendering a local `ImageBitmap` onto an offscreen canvas, then exporting as PNG.
 */
const convertToPngDataUrl = async (imageUrl) => {
    try {
        if (!imageUrl) return null;

        // If already a data URL string, return as is
        if (typeof imageUrl === 'string' && imageUrl.startsWith('data:')) {
            return imageUrl;
        }

        // Use the cache only for string URLs
        if (typeof imageUrl === 'string' && imageConversionCache.has(imageUrl)) {
            return imageConversionCache.get(imageUrl);
        }

        let blob;
        if (typeof imageUrl === 'string') {
            const proxyUrl = `?url=${encodeURIComponent(imageUrl)}`;
            const response = await gets3ImageBase64(proxyUrl);
            if (response.status !== 200) {
                throw new Error(`Image fetch HTTP ${response.status}`);
            }
            // Convert base64 string from Lambda response to Blob
            const base64Data = response.data;
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            blob = new Blob([bytes], { type: 'image/jpeg' });
        } else if (imageUrl instanceof Blob) {
            // File/Blob provided from input
            blob = imageUrl;
        } else {
            // Unknown type; cannot convert
            return null;
        }

        // Create bitmap from blob for broad format support
        const bitmap = await createImageBitmap(blob).catch(() => null);
        if (!bitmap) {
            // Fallback: try FileReader to data URL (may still work if browser supports the format)
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            }).catch(() => null);
            if (!dataUrl) return null;
            if (typeof imageUrl === 'string') {
                imageConversionCache.set(imageUrl, dataUrl);
            }
            return dataUrl;
        }

        // Draw onto canvas and export PNG data URL
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bitmap, 0, 0);
        const pngDataUrl = canvas.toDataURL('image/png');

        if (typeof imageUrl === 'string') {
            imageConversionCache.set(imageUrl, pngDataUrl);
        }
        return pngDataUrl;
    } catch (err) {
        const label = typeof imageUrl === 'string' ? imageUrl.slice(0, 120) : '[blob]';
        console.log('Image conversion failed:', label, err.message);
        return null;
    }
};

/**
 * Compute Difference Hash (dHash) from a PNG data URL
 * Standard: resize to 9x8 (for 8x8 differences → 64 bits)
 */
const getDHash = async (imageDataUrl) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Resize to 9 width x 8 height for classic dHash
                canvas.width = 9;
                canvas.height = 8;
                ctx.drawImage(img, 0, 0, 9, 8);

                const imageData = ctx.getImageData(0, 0, 9, 8);
                const pixels = imageData.data;

                // Convert to grayscale (luminance)
                const gray = [];
                for (let i = 0; i < pixels.length; i += 4) {
                    const r = pixels[i];
                    const g = pixels[i + 1];
                    const b = pixels[i + 2];
                    gray.push(Math.round(0.299 * r + 0.587 * g + 0.114 * b));
                }

                // Compute differences (left-to-right per row)
                let hash = '';
                let idx = 0;
                for (let y = 0; y < 8; y++) {
                    for (let x = 0; x < 8; x++) {  // 8 comparisons per row
                        const left = gray[idx];
                        const right = gray[idx + 1];
                        hash += (left > right ? '1' : '0');
                        idx++;
                    }
                    idx++;  // Skip to next row (we have 9 columns)
                }

                resolve(hash);  // 64-character binary string
            } catch (err) {
                console.log("dHash error:", err.message);
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = imageDataUrl;
    });
};

/**
 * Hamming distance between two 64-bit binary string hashes
 */
const hammingDistance = (hash1, hash2) => {
    if (!hash1 || !hash2 || hash1.length !== hash2.length) return Infinity;
    let dist = 0;
    for (let i = 0; i < hash1.length; i++) {
        if (hash1[i] !== hash2[i]) dist++;
    }
    return dist;
};

export const FindPets = async (pets, imageFiles, type) => {

    try {

        // If images are provided, attempt to auto-detect the animal type
        // from the first image. If it differs from `type`, prefer detected type
        // and log a warning for the mismatch.
        if (imageFiles && imageFiles.length > 0) {
            const firstImg = imageFiles[0];
            const firstPng = typeof firstImg === 'string' && firstImg.startsWith('data:')
                ? firstImg
                : await convertToPngDataUrl(firstImg);
        }

        const filteredPets = pets.filter(pet => pet.animal === type);

        // Normalize found images to PNG data URLs before histogram
        const foundPngs = await Promise.all(
            imageFiles.map(async (img) => {
                // `img` may already be a data URL from file input
                if (typeof img === 'string' && img.startsWith('data:')) return img;
                const converted = await convertToPngDataUrl(img);
                return converted || null;
            })
        );

        const foundDHashes = await Promise.all(
            foundPngs.map(async (img) => {
                if (!img) return null;
                const hash = await getDHash(img);
                return hash;
            })
        );

        const matches = [];
        // Compare with each lost pet
        for (const lostPet of filteredPets) {
            if (!lostPet.breedimage || lostPet.breedimage.length === 0) continue;
            let minHammingDist = Infinity;

            // Prepare lost pet image PNG conversions (limit to first 3)
            const lostPngs = await Promise.all(
                lostPet.breedimage.slice(0, 3).map((url) => convertToPngDataUrl(url))
            );
            // Precompute dHashes for lost images
            const lostDHashes = await Promise.all(
                lostPngs.map(async (png, i) => {
                    if (!png) return null;
                    const hash = await getDHash(png);
                    return hash;
                })
            );
            // Compare each found image dHash with each lost image dHash
            for (const foundHash of foundDHashes) {
                if (!foundHash) continue; // Skip if dHash computation failed
                for (const lostHash of lostDHashes) {
                    if (!lostHash) continue;
                    const dist = hammingDistance(foundHash, lostHash);
                    if (dist < minHammingDist) {
                        minHammingDist = dist;
                        // Early exit if very low distance
                        if (minHammingDist <= 5) break;
                    }
                }
                if (minHammingDist <= 5) break;
            }
            // Only add matches with Hamming distance <= 20
            if (minHammingDist <= 30) {
                // Convert distance to similarity score (0-1)
                const similarity = (64 - minHammingDist) / 64;
                const score = 20 + (similarity * 80);
                matches.push({
                    lostPet,
                    // Include an explicit pet id for convenience
                    petId: lostPet.id || lostPet._id || lostPet.petId || null,
                    score: Number(score.toFixed(1)),
                    imageSimilarity: Number(similarity.toFixed(3))
                });
            }
        }

        // Sort by score (highest first)
        const sortedMatches = matches.sort((a, b) => b.score - a.score);

        // Log a compact summary with pet id and similarity score
        sortedMatches.forEach(m => {
            const id = m.petId ?? (m.lostPet?.id || m.lostPet?._id || m.lostPet?.petId || 'unknown');
        });

        return sortedMatches;

    } catch (error) {
        console.error("Error finding pet matches:", error);

        // Fallback: Return all matching animal type
        const fallbackMatches = pets
            .filter(pet => pet.animal === type)
            .map(lostPet => ({
                lostPet,
                score: 20,
                imageSimilarity: 0,
                fallback: true
            }));

        return fallbackMatches;
    }
};

